import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  BuildingLibraryIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Reference, ReferenceFormData, CommissionStructure } from '../types/Reference';
import { referenceApi } from '../services/referenceApi';

interface ReferenceFormProps {
  reference?: Reference | null;
  onClose: () => void;
  onSave: (reference: Reference) => void;
}

const ReferenceForm: React.FC<ReferenceFormProps> = ({ reference, onClose, onSave }) => {
  const [formData, setFormData] = useState<ReferenceFormData>({
    name: '',
    contactNumber: '',
    email: '',
    address: '',
    personalImage: undefined,
    bankAccountNumber: '',
    bankAccountName: '',
    branchName: '',
    bankAccountRoutingNumber: '',
    bankImage: undefined,
    commissionStructure: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [personalImagePreview, setPersonalImagePreview] = useState<string | null>(null);
  const [bankImagePreview, setBankImagePreview] = useState<string | null>(null);
  const [availableStages, setAvailableStages] = useState<any[]>([]);

  useEffect(() => {
    // Fetch available stages
    const fetchStages = async () => {
      try {
        const response = await referenceApi.getAvailableStages();
        if (response.success) {
          setAvailableStages(response.data);
        }
      } catch (error) {
        console.error('Error fetching stages:', error);
      }
    };
    
    fetchStages();
    
    if (reference) {
      setFormData({
        name: reference.name,
        contactNumber: reference.contactNumber,
        email: reference.email,
        address: reference.address,
        personalImage: undefined,
        bankAccountNumber: reference.bankAccountNumber,
        bankAccountName: reference.bankAccountName,
        branchName: reference.branchName,
        bankAccountRoutingNumber: reference.bankAccountRoutingNumber,
        bankImage: undefined,
        commissionStructure: reference.commissionStructure || []
      });
      
      if (reference.imageUrl) {
        setPersonalImagePreview(reference.imageUrl);
      }
      if (reference.bankDocumentUrl) {
        setBankImagePreview(reference.bankDocumentUrl);
      }
    }
  }, [reference]);

  const handleInputChange = (field: keyof ReferenceFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCommissionChange = (stageId: number, value: number) => {
    setFormData(prev => {
      const existingIndex = prev.commissionStructure.findIndex(cs => cs.stageId === stageId);
      const updatedStructure = [...prev.commissionStructure];
      
      if (existingIndex >= 0) {
        updatedStructure[existingIndex] = {
          ...updatedStructure[existingIndex],
          commissionAmount: value
        };
      } else {
        const stage = availableStages.find(s => s.id === stageId);
        if (stage) {
          updatedStructure.push({
            stageId: stageId,
            stageName: stage.name,
            commissionAmount: value
          });
        }
      }
      
      return {
        ...prev,
        commissionStructure: updatedStructure
      };
    });
  };

  const handleImageChange = (field: 'personalImage' | 'bankImage', file: File | null) => {
    if (file) {
      setFormData(prev => ({ ...prev, [field]: file }));
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      if (field === 'personalImage') {
        setPersonalImagePreview(previewUrl);
      } else {
        setBankImagePreview(previewUrl);
      }
    }
  };

  const removeImage = (field: 'personalImage' | 'bankImage') => {
    setFormData(prev => ({ ...prev, [field]: undefined }));
    if (field === 'personalImage') {
      setPersonalImagePreview(null);
    } else {
      setBankImagePreview(null);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Personal Information validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // Bank Details validation
    if (!formData.bankAccountNumber.trim()) {
      newErrors.bankAccountNumber = 'Bank account number is required';
    }
    if (!formData.bankAccountName.trim()) {
      newErrors.bankAccountName = 'Bank account name is required';
    }
    if (!formData.branchName.trim()) {
      newErrors.branchName = 'Branch name is required';
    }
    if (!formData.bankAccountRoutingNumber.trim()) {
      newErrors.bankAccountRoutingNumber = 'Bank account routing number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      let response;
      
      if (reference) {
        response = await referenceApi.updateReference(reference.id, formData);
      } else {
        response = await referenceApi.createReference(formData);
      }

      if (response.success) {
        onSave(response.data);
      } else {
        console.error('Error saving reference:', response);
      }
    } catch (error) {
      console.error('Error saving reference:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-0 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {reference ? 'Edit Reference' : 'Add New Reference'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Personal Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.contactNumber}
                    onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.contactNumber ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter contact number"
                  />
                  {errors.contactNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.contactNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.address ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter complete address"
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personal Image
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('personalImage', e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                    {personalImagePreview && (
                      <div className="relative">
                        <img
                          src={personalImagePreview}
                          alt="Personal preview"
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage('personalImage')}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BuildingLibraryIcon className="w-5 h-5 mr-2" />
                Bank Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Account Number *
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccountNumber}
                    onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.bankAccountNumber ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter bank account number"
                  />
                  {errors.bankAccountNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.bankAccountNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Account Name *
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccountName}
                    onChange={(e) => handleInputChange('bankAccountName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.bankAccountName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter bank account name"
                  />
                  {errors.bankAccountName && (
                    <p className="mt-1 text-sm text-red-600">{errors.bankAccountName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch Name *
                  </label>
                  <input
                    type="text"
                    value={formData.branchName}
                    onChange={(e) => handleInputChange('branchName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.branchName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter branch name"
                  />
                  {errors.branchName && (
                    <p className="mt-1 text-sm text-red-600">{errors.branchName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Account Routing Number *
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccountRoutingNumber}
                    onChange={(e) => handleInputChange('bankAccountRoutingNumber', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.bankAccountRoutingNumber ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter routing number"
                  />
                  {errors.bankAccountRoutingNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.bankAccountRoutingNumber}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Document Image
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange('bankImage', e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                    {bankImagePreview && (
                      <div className="relative">
                        <img
                          src={bankImagePreview}
                          alt="Bank document preview"
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage('bankImage')}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Commission Structure */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Commission Structure
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {availableStages.map((stage) => {
                  const commission = formData.commissionStructure.find(cs => cs.stageId === stage.id);
                  const amount = commission ? commission.commissionAmount : 0;
                  
                  return (
                    <div key={stage.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                        {stage.name} Commission
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={amount}
                          onChange={(e) => handleCommissionChange(stage.id, parseFloat(e.target.value) || 0)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Set the commission amount for each lead stage. This will be used to calculate reference commissions.
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : (reference ? 'Update Reference' : 'Create Reference')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceForm;
